import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import posthog from 'posthog-js';

/**
 * Hook that monitors Clerk authentication state and manages PostHog user identification
 * - Identifies users when they sign in
 * - Updates user properties without PII
 * - Handles anonymous to identified transition
 * - Resets identification when users sign out
 */
export function usePostHogIdentification() {
  const { isSignedIn, user } = useUser();

  useEffect(() => {
    // Skip if PostHog is not initialized
    if (!posthog || typeof posthog.identify !== 'function') {
      return;
    }

    if (isSignedIn && user) {
      // User is signed in - identify them in PostHog
      const userId = user.id;
      
      // Prepare user properties without PII
      // Only include non-sensitive metadata
      const userProperties = {
        created_at: user.createdAt ? new Date(user.createdAt).toISOString() : undefined,
        // Use a hash of email domain for segmentation without exposing email
        email_domain_hash: user.primaryEmailAddress?.emailAddress 
          ? hashEmailDomain(user.primaryEmailAddress.emailAddress)
          : undefined,
        // Track auth methods without exposing actual accounts
        has_google_auth: user.externalAccounts?.some(acc => acc.provider === 'google') || false,
        has_github_auth: user.externalAccounts?.some(acc => acc.provider === 'github') || false,
        has_password_auth: user.passwordEnabled || false,
        // Track verification status
        email_verified: user.primaryEmailAddress?.verification?.status === 'verified' || false,
        // Custom metadata if available
        user_metadata: user.publicMetadata || {},
      };

      // Remove undefined values
      const cleanProperties = Object.fromEntries(
        Object.entries(userProperties).filter(([, value]) => value !== undefined)
      );

      // Identify the user
      posthog.identify(userId, cleanProperties);
      
      // Track successful sign in if this is a new session
      if (!sessionStorage.getItem('posthog_auth_tracked')) {
        posthog.capture('sign_in_success', {
          method: determineSignInMethod(user),
          timestamp: new Date().toISOString(),
        });
        sessionStorage.setItem('posthog_auth_tracked', 'true');
      }
    } else if (!isSignedIn && posthog.get_distinct_id()?.startsWith('user_')) {
      // User signed out but PostHog still has their ID - reset it
      posthog.reset();
      
      // Clear the auth tracking flag
      sessionStorage.removeItem('posthog_auth_tracked');
    }
  }, [isSignedIn, user]);
}

/**
 * Hash email domain for analytics without exposing the actual domain
 * This allows us to segment by email provider type without storing PII
 */
function hashEmailDomain(email: string): string {
  const domain = email.split('@')[1];
  if (!domain) return 'unknown';
  
  // Simple categorization instead of actual domain
  if (domain.includes('gmail') || domain.includes('googlemail')) return 'personal_google';
  if (domain.includes('yahoo') || domain.includes('ymail')) return 'personal_yahoo';
  if (domain.includes('hotmail') || domain.includes('outlook') || domain.includes('live')) return 'personal_microsoft';
  if (domain.includes('.edu')) return 'educational';
  if (domain.includes('.gov')) return 'government';
  
  // For all others, just mark as business or personal based on common patterns
  const personalDomains = ['aol', 'icloud', 'me.com', 'mac.com', 'protonmail', 'proton.me'];
  if (personalDomains.some(pd => domain.includes(pd))) return 'personal_other';
  
  return 'business';
}

/**
 * Determine the sign-in method used based on user data
 */
function determineSignInMethod(user: {
  externalAccounts?: Array<{ provider: string }>;
  passwordEnabled?: boolean;
  primaryEmailAddress?: {
    linkedTo?: Array<{ type: string }>;
  };
}): string {
  // Check external accounts first
  if (user.externalAccounts?.length > 0) {
    const latestAccount = user.externalAccounts[user.externalAccounts.length - 1];
    return `oauth_${latestAccount.provider}`;
  }
  
  // Check if password was used
  if (user.passwordEnabled) {
    return 'password';
  }
  
  // Check for magic link or other methods
  if (user.primaryEmailAddress?.linkedTo?.some((link) => link.type === 'magic_link')) {
    return 'magic_link';
  }
  
  return 'unknown';
}